import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import ModalHeader from '../../components/common/ModalHeader';
import FormField from '../../components/common/FormField';
import TimeFormField from '../../components/common/TimeFormField';
import ActionButtons from '../../components/common/ActionButtons';
import { useToastService } from '../../components/toast/ToastService';
import { DefaultWellTestFormData } from '../../types/wellTestTypes';
import { TimeValue } from '../../components/date-range-picker/types';
import './../add-well-test/addWellTestModal.scss';
import { useParams } from 'react-router-dom';
import { fetchDefaultWellTestDataAsync } from '../../dispatchers/DefaultWellTestDispatcher';
import { saveDefaultWellTestAsync } from '../../components/slices/DefaultWellTestUpdateSlice';
import { createPortal } from 'react-dom';

interface DefaultWellTestFormErrors {
  [key: string]: string;
}

interface DefaultWellTestTouchedFields {
  [key: string]: boolean;
}

interface AddDefaultWellTestModalProps {
  modalTitle: string;
  buttonText: string;
  onClose: () => void;
  initialData?: Partial<DefaultWellTestFormData>;
  disabled?: boolean;
}

export default function AddDefaultWellTestModal({
  modalTitle,
  buttonText,
  onClose,
  initialData,
  disabled = false,
}: AddDefaultWellTestModalProps) {
  const { assetId } = useParams<{ assetId: string }>();
  const [formData, setFormData] = useState<DefaultWellTestFormData>({
    testDate: '',
    duration: '',
    oilRate: '',
    waterRate: '',
    gasRate: '',
    tubingPressure: '',
    casingPressure: '',
    runtime: '',
    strokeLength: '',
    fluidAbovePump: '',
    pumpsize: '',
    spm: '',
    flowlineTemp: '',
    casingTemp: '',
    waterSG: '',
    oilAPIGravity: '',
    co2: '',
    highPressureGasRate: '',
    casingGasRate: '',
    gasLiftGasRate: '',
    flowlinePressure: '',
    tubingTemp: '',
    pumpIntakePressure: '',
    pumpDischargePressure: '',
    pumpIntakeTemp: '',
    motorTemp: '',
    driveCurrent: '',
    driveSpeed: '',
    rodRPM: '',
    rodTorque: '',
    triplexDischargePressure: '',
    suctionPressure: '',
    linePressureToSetting: '',
    wellId: null,
    id: 0,
    ...initialData,
  });

  const dispatch = useAppDispatch();
  const { showSuccessToast, showErrorToast } = useToastService();

  const [errors, setErrors] = useState<DefaultWellTestFormErrors>({});
  const [touched, setTouched] = useState<DefaultWellTestTouchedFields>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testTime, setTestTime] = useState<TimeValue | null>(null);

  // Extract time from initial testDate if provided
  useEffect(() => {
    if (initialData?.testDate) {
      try {
        const date = new Date(initialData.testDate);
        if (!isNaN(date.getTime())) {
          const hours12 = date.getHours() % 12 || 12;
          const period = date.getHours() >= 12 ? 'PM' : 'AM';
          setTestTime({
            hours: hours12.toString().padStart(2, '0'),
            minutes: date.getMinutes().toString().padStart(2, '0'),
            seconds: date.getSeconds().toString().padStart(2, '0'),
            period: period,
          });
        }
      } catch (error) {
        console.error('Error parsing initial time:', error);
      }
    }
  }, [initialData?.testDate]);

  const testWellData = useAppSelector((state: any) => state.testWellDataSlice?.data);

  const disabledDates = useMemo(
    () =>
      (testWellData ?? []).map((item: any) => {
        const date = new Date(item.TestDate || item.Date);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }),
    [testWellData],
  );

  // List of mandatory fields
  const mandatoryFields = ['testDate', 'duration', 'oilRate', 'waterRate', 'gasRate'];

  // Validation configuration object
  const validationConfig = {
    duration: { min: 0.1, max: 1000, unit: 'hrs' },
    oilRate: { min: 0, max: 1500, unit: 'stb/d' },
    waterRate: { min: 0, max: 5000, unit: 'stb/d' },
    gasRate: { min: 0, max: 3000, unit: 'mcf/d' },
    // tubingPressure: { min: 0, max: 0, unit: 'psig' },
    // casingPressure: { min: 0, max: 0, unit: 'psig' },
    runtime: { min: 0, max: 24, unit: 'hrs' },
    strokeLength: { min: 0, max: 256, unit: 'inches' },
    fluidAbovePump: { min: 0, max: 12000, unit: 'ft' },
    pumpsize: { min: 0.75, max: 3.75, unit: 'inches' },
    spm: { min: 0, max: 20, unit: '' },
    flowlineTemp: { min: -50, max: 300, unit: 'deg F' },
    casingTemp: { min: -50, max: 300, unit: 'deg F' },
    // waterSG: { min: 0.8, max: 1.5, unit: '' },
    oilAPIGravity: { min: 6, max: 55, unit: 'API' },
    // co2: { min: 0, max: 1, unit: '' },
    // highPressureGasRate: { min: 0, max: 100000, unit: 'scf/d' },
    // casingGasRate: { min: 0, max: 100000, unit: 'scf/d' },
    // gasLiftGasRate: { min: 0, max: 100000, unit: 'scf/d' },
    flowlinePressure: { min: 0, max: 5000, unit: 'psig' },
    // tubingTemp: { min: 32, max: 500, unit: 'deg F' },
    // pumpIntakePressure: { min: 0, max: 5000, unit: 'psig' },
    // pumpDischargePressure: { min: 0, max: 5000, unit: 'psig' },
    // pumpIntakeTemp: { min: 32, max: 500, unit: 'deg F' },
    // motorTemp: { min: 32, max: 500, unit: 'deg F' },
    // driveCurrent: { min: 0, max: 500, unit: 'amps' },
    // driveSpeed: { min: 0, max: 10000, unit: 'RPM' },
    // rodRPM: { min: 0, max: 100, unit: 'RPM' },
    // rodTorque: { min: 0, max: 10000, unit: 'ft-lbs' },
    // triplexDischargePressure: { min: 0, max: 8000, unit: 'psig' },
    // suctionPressure: { min: 0, max: 5000, unit: 'psig' },
    // linePressureToSetting: { min: 0, max: 5000, unit: 'psig' },
  };

  // Function to get help text for a field
  const getHelpText = (fieldName: string): string => {
    const config = validationConfig[fieldName as keyof typeof validationConfig];
    if (!config) return '';
    const unit = config.unit ? ` ${config.unit}` : '';
    return `Must be between ${config.min} and ${config.max}${unit}`;
  };

  const validateField = (name: string, value: string): string => {
    // Check if field is mandatory and empty
    if (mandatoryFields.includes(name) && value === '') {
      return 'This field is required';
    }

    // If not empty, validate based on field-specific rules
    if (value === '') return '';

    const validateNumericField = (value: string, min: number, max: number, errorMessage: string): string | null => {
      if (!/^\d*\.?\d+$/.test(value)) {
        return errorMessage;
      }
      const numericValue = parseFloat(value);
      if (isNaN(numericValue) || numericValue < min || numericValue > max) {
        return errorMessage;
      }
      return null;
    };

    // Check if field has validation config
    const config = validationConfig[name as keyof typeof validationConfig];
    if (config) {
      return validateNumericField(value, config.min, config.max, getHelpText(name)) || '';
    }

    return '';
  };

  const handleInputChange = (name: string, value: string | number | Date | null) => {
    if (value === null || value instanceof Date) {
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    // Mark field as touched
    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true,
      });
    }

    const error = validateField(name, String(value));

    if (error) {
      setErrors({
        ...errors,
        [name]: error,
      });
    } else {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }

    if (name === 'testDate' && value) {
      const newErrors = { ...errors };
      delete newErrors.testDate;
      setErrors(newErrors);
    }
  };

  const handleTimeChange = (_name: string, value: TimeValue | null) => {
    setTestTime(value);

    // Clear time-related errors if they exist
    if (errors.testTime) {
      const newErrors = { ...errors };
      delete newErrors.testTime;
      setErrors(newErrors);
    }
  };

  const formatValue = (value: string | number | null | undefined): string | null => {
    if (value === null || value === undefined) return null;
    return typeof value === 'number' ? value.toString() : value;
  };

  const handleBlur = (name: string) => {
    // Mark field as touched on blur
    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true,
      });

      // Validate on blur for mandatory fields
      if (mandatoryFields.includes(name) && formData[name as keyof typeof formData] === '') {
        setErrors({
          ...errors,
          [name]: 'This field is required',
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors = { ...errors };
    let isValid = true;

    // Check all mandatory fields
    mandatoryFields.forEach((field) => {
      if (formData[field as keyof typeof formData] === '') {
        newErrors[field] = 'This field is required';
        isValid = false;
      }
    });

    // Also validate all non-empty fields against their validation rules
    Object.keys(formData).forEach((field) => {
      const value = formData[field as keyof typeof formData];
      if (value !== '' && value !== null && value !== undefined) {
        const validationError = validateField(field, String(value));
        if (validationError) {
          newErrors[field] = validationError;
          isValid = false;
        }
      }
    });

    // Check if there are any actual error messages in newErrors
    const hasErrors = Object.values(newErrors).some((error) => error !== '');
    if (hasErrors) {
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const [isModalOpen, setIsModalOpen] = useState(true);

  const handleCancel = () => {
    setIsModalOpen(false);
    onClose();
  };

  const handleConfirmSave = async () => {
    // Mark all mandatory fields as touched
    const newTouched = { ...touched };
    mandatoryFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    // Validate the form before proceeding
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Combine date and time into a single datetime string for backend
      let combinedDateTime = formData.testDate;
      if (formData.testDate && testTime) {
        const dateObj = new Date(formData.testDate);
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        const yyyy = dateObj.getFullYear();
        const datePart = `${mm}/${dd}/${yyyy}`;
        const timePart = `${testTime.hours}:${testTime.minutes}:${testTime.seconds} ${testTime.period}`;
        combinedDateTime = `${datePart} ${timePart}`;
      }

      const updatedFormData = {
        ...formData,
        testDate: combinedDateTime,
      };
      const isEdit = initialData != null;
      const resultAction = await dispatch(
        saveDefaultWellTestAsync({ formData: updatedFormData, assetId: assetId ?? '', isEdit: isEdit }),
      );

      if (saveDefaultWellTestAsync.fulfilled.match(resultAction)) {
        const successMessage = isEdit ? 'Well Test Successfully updated' : 'Well Test Successfully saved';
        showSuccessToast(successMessage);
        setIsModalOpen(false);
        dispatch(fetchDefaultWellTestDataAsync(assetId ?? ''));
        onClose();
      } else {
        const errorTitle = isEdit ? 'Well Test update failure' : 'Well Test save failure';
        const errorMessage = isEdit
          ? 'There was an error updating the well test. Please try again.'
          : 'There was an error saving the well test. Please try again.';
        showErrorToast(errorTitle, errorMessage);
      }
    } catch (error) {
      const isEdit = formData.id && formData.id > 0;
      const errorTitle = isEdit ? 'Well Test update failure' : 'Well Test save failure';
      const errorMessage = isEdit
        ? 'There was an error updating the well test. Please try again.'
        : 'There was an error saving the well test. Please try again.';
      showErrorToast(errorTitle, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isModalOpen) {
    return null;
  }

  return createPortal(
    <div className='modal-overlay'>
      <div className='modal-container'>
        <div className='modal-main-container'>
          <ModalHeader title={modalTitle} onClose={handleCancel} />

          <main className='form-main'>
            <section className='form-section'>
              <div className='form-row'>
                <FormField
                  label='Test Date'
                  name='testDate'
                  value={formData.testDate ? new Date(formData.testDate) : null}
                  onChange={(name, value) => {
                    if (value instanceof Date) {
                      // maintain MM/dd/yyyy for internal state as required by validation/backend
                      const mm = String(value.getMonth() + 1).padStart(2, '0');
                      const dd = String(value.getDate()).padStart(2, '0');
                      const yyyy = value.getFullYear();
                      const formattedDate = `${mm}/${dd}/${yyyy}`;
                      handleInputChange(name, formattedDate);
                    } else {
                      handleInputChange(name, value);
                    }
                  }}
                  error={touched.testDate ? errors.testDate : ''}
                  type='date'
                  required={true}
                  disabled={disabled}
                  disabledDates={disabledDates}
                />
                <TimeFormField
                  label='Test Time *'
                  name='testTime'
                  value={testTime}
                  onChange={handleTimeChange}
                  error={touched.testTime ? errors.testTime : ''}
                  disabled={disabled}
                />
              </div>
              <div className='form-row'>
                <FormField
                  label='Oil Rate (stb/d) *'
                  name='oilRate'
                  value={formatValue(formData.oilRate)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('oilRate')}
                  error={touched.oilRate ? errors.oilRate : ''}
                />
                <FormField
                  label='Water Rate (stb/d) *'
                  name='waterRate'
                  value={formatValue(formData.waterRate)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('waterRate')}
                  error={touched.waterRate ? errors.waterRate : ''}
                />
                <FormField
                  label='Gas Rate (mcf/d) *'
                  name='gasRate'
                  value={formatValue(formData.gasRate)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('gasRate')}
                  error={touched.gasRate ? errors.gasRate : ''}
                />
              </div>
              <div className='form-row'>
                <FormField
                  label='Tubing Pressure (psig)'
                  name='tubingPressure'
                  value={formatValue(formData.tubingPressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('tubingPressure')}
                  error={touched.tubingPressure ? errors.tubingPressure : ''}
                />
                <FormField
                  label='Casing Pressure (psig)'
                  name='casingPressure'
                  value={formatValue(formData.casingPressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('casingPressure')}
                  error={touched.casingPressure ? errors.casingPressure : ''}
                />
                <FormField
                  label='Flowline Pressure (psig)'
                  name='flowlinePressure'
                  value={formatValue(formData.flowlinePressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('flowlinePressure')}
                  error={touched.flowlinePressure ? errors.flowlinePressure : ''}
                />
              </div>
              <div className='form-row'>
                <FormField
                  label='Duration (hrs) *'
                  name='duration'
                  value={formatValue(formData.duration)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('duration')}
                  error={touched.duration ? errors.duration : ''}
                />
                <FormField
                  label='Runtime (hrs)'
                  name='runtime'
                  value={formatValue(formData.runtime)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('runtime')}
                  error={touched.runtime ? errors.runtime : ''}
                />
                <FormField
                  label='SPM'
                  name='spm'
                  value={formatValue(formData.spm)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('spm')}
                  error={touched.spm ? errors.spm : ''}
                />
              </div>
              <div className='form-row'>
                <FormField
                  label='Stroke Length (in)'
                  name='strokeLength'
                  value={formatValue(formData.strokeLength)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('strokeLength')}
                  error={touched.strokeLength ? errors.strokeLength : ''}
                />
                <FormField
                  label='Fluid Above Pump (ft)'
                  name='fluidAbovePump'
                  value={formatValue(formData.fluidAbovePump)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('fluidAbovePump')}
                  error={touched.fluidAbovePump ? errors.fluidAbovePump : ''}
                />
                <FormField
                  label='Pump Size (in.)'
                  name='pumpsize'
                  value={formatValue(formData.pumpsize)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('pumpsize')}
                  error={touched.pumpsize ? errors.pumpsize : ''}
                />
              </div>
              <div className='form-row'>
                <FormField
                  label='Flowline Temp (deg F)'
                  name='flowlineTemp'
                  value={formatValue(formData.flowlineTemp)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('flowlineTemp')}
                  error={touched.flowlineTemp ? errors.flowlineTemp : ''}
                />
                <FormField
                  label='Casing Temp (deg F)'
                  name='casingTemp'
                  value={formatValue(formData.casingTemp)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('casingTemp')}
                  error={touched.casingTemp ? errors.casingTemp : ''}
                />
                <FormField
                  label='Oil API Gravity (s.g)'
                  name='oilAPIGravity'
                  value={formatValue(formData.oilAPIGravity)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('oilAPIGravity')}
                  error={touched.oilAPIGravity ? errors.oilAPIGravity : ''}
                />
              </div>
              <div className='form-row'>
                <FormField
                  label='Water SG (s.g.)'
                  name='waterSG'
                  value={formatValue(formData.waterSG)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText={getHelpText('waterSG')}
                  error={touched.waterSG ? errors.waterSG : ''}
                />
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                {/* <FormField
                                    label='Tubing Temp (deg F)'
                                    name='tubingTemp'
                                    value={formatValue(formData.tubingTemp)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('tubingTemp')}
                                    error={touched.tubingTemp ? errors.tubingTemp : ''}
                                />
                                <FormField
                                    label='CO2'
                                    name='co2'
                                    value={formatValue(formData.co2)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('co2')}
                                    error={touched.co2 ? errors.co2 : ''}
                                /> */}
              </div>
              {/* <div className='form-row'>
                                <FormField
                                    label='High Pressure Gas Rate'
                                    name='highPressureGasRate'
                                    value={formatValue(formData.highPressureGasRate)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('highPressureGasRate')}
                                    error={touched.highPressureGasRate ? errors.highPressureGasRate : ''}
                                />
                                <FormField
                                    label='Casing Gas Rate'
                                    name='casingGasRate'
                                    value={formatValue(formData.casingGasRate)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('casingGasRate')}
                                    error={touched.casingGasRate ? errors.casingGasRate : ''}
                                />
                                <FormField
                                    label='Gas Lift Gas Rate'
                                    name='gasLiftGasRate'
                                    value={formatValue(formData.gasLiftGasRate)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('gasLiftGasRate')}
                                    error={touched.gasLiftGasRate ? errors.gasLiftGasRate : ''}
                                />
                            </div> */}
              {/* <div className='form-row'>
                                <FormField
                                    label='Pump Intake Pressure (psig)'
                                    name='pumpIntakePressure'
                                    value={formatValue(formData.pumpIntakePressure)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('pumpIntakePressure')}
                                    error={touched.pumpIntakePressure ? errors.pumpIntakePressure : ''}
                                />
                                <FormField
                                    label='Pump Discharge Pressure (psig)'
                                    name='pumpDischargePressure'
                                    value={formatValue(formData.pumpDischargePressure)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('pumpDischargePressure')}
                                    error={touched.pumpDischargePressure ? errors.pumpDischargePressure : ''}
                                />
                                <FormField
                                    label='Pump Intake Temp (deg F)'
                                    name='pumpIntakeTemp'
                                    value={formatValue(formData.pumpIntakeTemp)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('pumpIntakeTemp')}
                                    error={touched.pumpIntakeTemp ? errors.pumpIntakeTemp : ''}
                                />
                            </div> */}
              {/* <div className='form-row'>
                                <FormField
                                    label='Motor Temp (deg F)'
                                    name='motorTemp'
                                    value={formatValue(formData.motorTemp)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('motorTemp')}
                                    error={touched.motorTemp ? errors.motorTemp : ''}
                                />
                                <FormField
                                    label='Drive Current'
                                    name='driveCurrent'
                                    value={formatValue(formData.driveCurrent)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('driveCurrent')}
                                    error={touched.driveCurrent ? errors.driveCurrent : ''}
                                />
                                <FormField
                                    label='Drive Speed'
                                    name='driveSpeed'
                                    value={formatValue(formData.driveSpeed)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('driveSpeed')}
                                    error={touched.driveSpeed ? errors.driveSpeed : ''}
                                />
                            </div> */}
              {/* <div className='form-row'>
                                <FormField
                                    label='Rod RPM'
                                    name='rodRPM'
                                    value={formatValue(formData.rodRPM)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('rodRPM')}
                                    error={touched.rodRPM ? errors.rodRPM : ''}
                                />
                                <FormField
                                    label='Rod Torque'
                                    name='rodTorque'
                                    value={formatValue(formData.rodTorque)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('rodTorque')}
                                    error={touched.rodTorque ? errors.rodTorque : ''}
                                />
                                <FormField
                                    label='Triplex Discharge Pressure (psig)'
                                    name='triplexDischargePressure'
                                    value={formatValue(formData.triplexDischargePressure)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('triplexDischargePressure')}
                                    error={touched.triplexDischargePressure ? errors.triplexDischargePressure : ''}
                                />
                            </div> */}
              {/* <div className='form-row'>
                                <FormField
                                    label='Suction Pressure (psig)'
                                    name='suctionPressure'
                                    value={formatValue(formData.suctionPressure)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('suctionPressure')}
                                    error={touched.suctionPressure ? errors.suctionPressure : ''}
                                />
                                <FormField
                                    label='Line Pressure To Setting (psig)'
                                    name='linePressureToSetting'
                                    value={formatValue(formData.linePressureToSetting)}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    helpText={getHelpText('linePressureToSetting')}
                                    error={touched.linePressureToSetting ? errors.linePressureToSetting : ''}
                                />
                            </div> */}
            </section>
          </main>
          <footer className='form-footer'>
            <ActionButtons
              buttonText={buttonText}
              onSubmit={handleConfirmSave}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
            />
          </footer>
        </div>
      </div>
    </div>,
    document.querySelector('.outlet-container') || document.body,
  );
}
